import React, { useEffect, useRef, useState } from 'react';
import { adminPreviewCompanyFilter } from '../../api/admin';
import type { FilterPreview } from '../../api/admin';
import type { Company, CompanyFilterConfig, StatusGroup } from '../../types/auth';
import { PERIODS, PERIOD_LABELS, TH } from './shared';

// Moderner, in Abschnitte gegliederter Unternehmens-Editor mit Live-Filter-Test.
// Ersetzt das frühere „alles auf einer Seite"-Formular.

type Section = 'stammdaten' | 'filter' | 'darstellung' | 'sitzung';

const SECTIONS: { id: Section; label: string }[] = [
  { id: 'stammdaten', label: 'Stammdaten' },
  { id: 'filter', label: 'Nummern & Filter' },
  { id: 'darstellung', label: 'Darstellung' },
  { id: 'sitzung', label: 'Sitzung & Gruppe' },
];

export interface CompanySaveData {
  name: string;
  logoSvg: string | null;
  groupId: string | null;
  filterConfig: Partial<CompanyFilterConfig>;
}

interface Props {
  initial?: Company;
  /** Vorbelegung für „Duplizieren": Konfiguration einer bestehenden Firma, aber als Neuanlage. */
  template?: Company;
  groups: StatusGroup[];
  onSave: (data: CompanySaveData) => Promise<void>;
  onCancel: () => void;
}

export default function CompanyEditor({ initial, template, groups, onSave, onCancel }: Props) {
  const source = initial ?? template;
  const [section, setSection] = useState<Section>('stammdaten');
  const [name, setName] = useState(initial ? initial.name : template ? `${template.name} (Kopie)` : '');
  const [logoSvg, setLogoSvg] = useState(source?.logoSvg ?? '');
  const [groupId, setGroupId] = useState(source?.groupId ?? '');
  const [period, setPeriod] = useState<CompanyFilterConfig['defaultPeriod']>(source?.filterConfig.defaultPeriod ?? 'THIS_MONTH');
  const [count, setCount] = useState(source?.filterConfig.defaultCount ?? 50);
  const [missedOnly, setMissedOnly] = useState(source?.filterConfig.missedOnly ?? false);
  const [prefixes, setPrefixes] = useState((source?.filterConfig.inboundPrefixes ?? []).join('\n'));
  const [outPrefixes, setOutPrefixes] = useState((source?.filterConfig.outboundPrefixes ?? []).join('\n'));
  const [allowedPeriods, setAllowedPeriods] = useState<Set<string>>(new Set(source?.filterConfig.allowedPeriods ?? PERIODS));
  const [defaultFields, setDefaultFields] = useState((source?.filterConfig.defaultFields ?? ['startDateTime', 'inboundSourceDialString', 'outboundDestinationDialString']).join(', '));
  const [lockFields, setLockFields] = useState(source?.filterConfig.lockFields ?? false);
  const [incomingNodes, setIncomingNodes] = useState((source?.filterConfig.incomingNodes ?? []).join('\n'));
  const [outgoingNodes, setOutgoingNodes] = useState((source?.filterConfig.outgoingNodes ?? []).join('\n'));
  const [sessionDurationDays, setSessionDurationDays] = useState(source?.filterConfig.sessionDurationDays ?? 1);
  const [saving, setSaving] = useState(false);

  const splitLines = (s: string) => s.split('\n').map((x) => x.trim()).filter(Boolean);

  const currentFilterConfig = (): Partial<CompanyFilterConfig> => ({
    defaultPeriod: period, defaultCount: count, missedOnly, lockFields, sessionDurationDays,
    inboundPrefixes: splitLines(prefixes),
    outboundPrefixes: splitLines(outPrefixes),
    allowedPeriods: Array.from(allowedPeriods) as CompanyFilterConfig['allowedPeriods'],
    defaultFields: defaultFields.split(',').map((s) => s.trim()).filter(Boolean),
    incomingNodes: splitLines(incomingNodes),
    outgoingNodes: splitLines(outgoingNodes),
  });

  async function handleSave() {
    if (!name.trim()) { setSection('stammdaten'); return; }
    setSaving(true);
    try {
      await onSave({
        name: name.trim(),
        logoSvg: logoSvg.trim() || null,
        groupId: groupId || null,
        filterConfig: currentFilterConfig(),
      });
    } finally { setSaving(false); }
  }

  // ── Filter-Test ──
  const [previewPeriod, setPreviewPeriod] = useState('THIS_MONTH');
  const [preview, setPreview] = useState<FilterPreview | null>(null);
  const [previewBusy, setPreviewBusy] = useState(false);
  const [previewError, setPreviewError] = useState('');
  const previewRetry = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => () => { if (previewRetry.current) clearTimeout(previewRetry.current); }, []);

  async function runPreview(retriesLeft = 24) {
    if (previewRetry.current) { clearTimeout(previewRetry.current); previewRetry.current = null; }
    setPreviewBusy(true);
    setPreviewError('');
    try {
      const result = await adminPreviewCompanyFilter(currentFilterConfig(), previewPeriod);
      setPreview(result);
      if (result.warming && retriesLeft > 0) {
        // Cache wird noch aufgebaut — automatisch nachfassen
        previewRetry.current = setTimeout(() => { void runPreview(retriesLeft - 1); }, 5000);
      } else {
        setPreviewBusy(false);
      }
    } catch {
      setPreviewError('Vorschau fehlgeschlagen — Anynode-API erreichbar?');
      setPreviewBusy(false);
    }
  }

  const logoDataUrl = logoSvg.trim()
    ? `data:image/svg+xml;charset=utf-8,${encodeURIComponent(logoSvg.trim())}`
    : null;

  function togglePeriod(p: string) {
    setAllowedPeriods((prev) => { const next = new Set(prev); if (next.has(p)) next.delete(p); else next.add(p); return next; });
  }

  const textareaHint = 'je Zeile ein Eintrag';

  return (
    <div className="card border-0 shadow-sm rounded-3">
      <div className="card-header bg-body d-flex align-items-center justify-content-between flex-wrap gap-2 py-3">
        <div>
          <h5 className="mb-0 fw-bold">{initial ? `Unternehmen bearbeiten` : 'Neues Unternehmen'}</h5>
          <span className="text-muted small">{initial ? initial.name : template ? `Vorlage: ${template.name}` : 'Stammdaten, Filter und Darstellung konfigurieren'}</span>
        </div>
        <ul className="nav nav-pills nav-sm gap-1">
          {SECTIONS.map((s) => (
            <li key={s.id} className="nav-item">
              <button
                className={`nav-link py-1 px-3 ${section === s.id ? 'active' : ''}`}
                style={{ fontSize: '0.83rem' }}
                onClick={() => setSection(s.id)}
              >
                {s.label}
              </button>
            </li>
          ))}
        </ul>
      </div>

      <div className="card-body" style={{ minHeight: 380 }}>
        {section === 'stammdaten' && (
          <div className="row g-3" style={{ maxWidth: 900 }}>
            <div className="col-12 col-md-6">
              <label className="form-label small fw-semibold">Name *</label>
              <input className="form-control" value={name} onChange={(e) => setName(e.target.value)} autoFocus required />
              {!name.trim() && <div className="form-text text-danger">Name ist erforderlich.</div>}
            </div>
            <div className="col-12">
              <label className="form-label small fw-semibold">Logo SVG <span className="fw-normal text-muted">(SVG-Code einfügen, leer = Standard)</span></label>
              <textarea className="form-control form-control-sm font-monospace" rows={4} value={logoSvg} onChange={(e) => setLogoSvg(e.target.value)} placeholder='<svg xmlns="http://www.w3.org/2000/svg" ...>...</svg>' />
            </div>
            {logoDataUrl && (
              <div className="col-12">
                <label className="form-label small fw-semibold">Logo-Vorschau</label>
                <div className="d-flex align-items-center gap-3 p-2 border rounded-2" style={{ background: 'var(--bs-body-bg)', width: 'fit-content' }}>
                  <img src={logoDataUrl} alt="40px" style={{ height: 40, width: 'auto', maxWidth: 160, objectFit: 'contain' }} />
                  <img src={logoDataUrl} alt="24px" style={{ height: 24, width: 'auto', maxWidth: 80, objectFit: 'contain', opacity: 0.7 }} />
                  <span className="text-muted small">40px · 24px</span>
                </div>
              </div>
            )}
          </div>
        )}

        {section === 'filter' && (
          <div className="row g-4">
            <div className="col-12 col-xl-6">
              <div className="row g-3">
                <div className="col-12 col-md-6">
                  <label className="form-label small fw-semibold">Eingehende Nummernpräfixe <span className="fw-normal text-muted">({textareaHint})</span></label>
                  <textarea className="form-control form-control-sm font-monospace" rows={5} value={prefixes} onChange={(e) => setPrefixes(e.target.value)} placeholder="+4922268308&#10;+4930" />
                  <div className="form-text">Anrufe <strong>an</strong> diese Nummern. „+49…", „0049…" und „0…" werden gleich behandelt.</div>
                </div>
                <div className="col-12 col-md-6">
                  <label className="form-label small fw-semibold">Ausgehende Nummernpräfixe <span className="fw-normal text-muted">({textareaHint})</span></label>
                  <textarea className="form-control form-control-sm font-monospace" rows={5} value={outPrefixes} onChange={(e) => setOutPrefixes(e.target.value)} placeholder="+4922268308&#10;+4930" />
                  <div className="form-text">Anrufe <strong>von</strong> diesen Nummern. Beide Filter zusammen: eingehend <em>oder</em> ausgehend.</div>
                </div>
                <div className="col-12 col-md-6">
                  <label className="form-label small fw-semibold">Node (Eingehend) <span className="fw-normal text-muted">(leer = alle)</span></label>
                  <textarea className="form-control form-control-sm font-monospace" rows={3} value={incomingNodes} onChange={(e) => setIncomingNodes(e.target.value)} placeholder="SIP-Trunk-A" />
                </div>
                <div className="col-12 col-md-6">
                  <label className="form-label small fw-semibold">Node (Ausgehend) <span className="fw-normal text-muted">(leer = alle)</span></label>
                  <textarea className="form-control form-control-sm font-monospace" rows={3} value={outgoingNodes} onChange={(e) => setOutgoingNodes(e.target.value)} placeholder="SIP-Trunk-A" />
                </div>
              </div>
            </div>

            {/* Live-Vorschau */}
            <div className="col-12 col-xl-6">
              <div className="border rounded-3 p-3 h-100" style={{ background: 'var(--bs-tertiary-bg)' }}>
                <div className="d-flex align-items-center gap-2 flex-wrap mb-2">
                  <span className="fw-semibold small">Filter testen</span>
                  <select className="form-select form-select-sm" style={{ width: 'auto' }} value={previewPeriod} onChange={(e) => setPreviewPeriod(e.target.value)}>
                    {PERIODS.map((p) => <option key={p} value={p}>{PERIOD_LABELS[p]}</option>)}
                  </select>
                  <button className="btn btn-primary btn-sm" disabled={previewBusy} onClick={() => runPreview()}>
                    {previewBusy ? <><span className="spinner-border spinner-border-sm me-1" />Lädt…</> : 'Vorschau laden'}
                  </button>
                </div>
                <div className="text-muted mb-2" style={{ fontSize: '0.72rem' }}>
                  Zeigt, was die aktuelle (ungespeicherte) Konfiguration ergeben würde.
                </div>

                {previewError && <div className="alert alert-danger py-2 small mb-2">{previewError}</div>}
                {preview?.warming && (
                  <div className="alert alert-info py-2 small mb-2">
                    Der Zeitraum-Cache wird noch aufgebaut — die Vorschau aktualisiert sich automatisch.
                  </div>
                )}
                {preview && (
                  <>
                    <div className="d-flex gap-2 flex-wrap mb-2">
                      <span className="badge bg-primary-subtle text-primary-emphasis">{preview.total.toLocaleString('de-DE')} Anrufe</span>
                      <span className="badge bg-warning-subtle text-warning-emphasis">{preview.missed.toLocaleString('de-DE')} verpasst</span>
                      {preview.oldest && <span className="badge bg-secondary-subtle text-secondary-emphasis">ältester: {String(preview.oldest).slice(0, 16)}</span>}
                    </div>
                    {preview.sample.length > 0 ? (
                      <div className="table-responsive">
                        <table className="table table-sm mb-0" style={{ fontSize: '0.72rem' }}>
                          <thead style={TH}>
                            <tr><th>Zeit</th><th>Von</th><th>An</th><th>Ergebnis</th></tr>
                          </thead>
                          <tbody>
                            {preview.sample.map((s, i) => (
                              <tr key={i}>
                                <td className="text-nowrap">{String(s.startDateTime).slice(5, 16)}</td>
                                <td className="font-monospace">{s.von || '—'}</td>
                                <td className="font-monospace">{s.an || '—'}</td>
                                <td>{s.result}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    ) : (
                      !preview.warming && <div className="text-muted small">Keine Anrufe im Zeitraum für diese Konfiguration.</div>
                    )}
                  </>
                )}
              </div>
            </div>
          </div>
        )}

        {section === 'darstellung' && (
          <div className="row g-3" style={{ maxWidth: 900 }}>
            <div className="col-6 col-md-3">
              <label className="form-label small fw-semibold">Standard-Zeitraum</label>
              <select className="form-select form-select-sm" value={period} onChange={(e) => setPeriod(e.target.value as typeof period)}>
                {PERIODS.map((p) => <option key={p} value={p}>{PERIOD_LABELS[p]}</option>)}
              </select>
            </div>
            <div className="col-6 col-md-3">
              <label className="form-label small fw-semibold">Standard-Anzahl</label>
              <input type="number" className="form-control form-control-sm" min={1} max={500} value={count} onChange={(e) => setCount(Number(e.target.value))} />
            </div>
            <div className="col-12 col-md-6">
              <label className="form-label small fw-semibold">Erlaubte Zeiträume</label>
              <div className="d-flex gap-3 flex-wrap pt-1">
                {PERIODS.map((p) => (
                  <div key={p} className="form-check">
                    <input className="form-check-input" type="checkbox" id={`ap-${p}`} checked={allowedPeriods.has(p)} onChange={() => togglePeriod(p)} />
                    <label className="form-check-label small" htmlFor={`ap-${p}`}>{PERIOD_LABELS[p]}</label>
                  </div>
                ))}
              </div>
            </div>
            <div className="col-12">
              <label className="form-label small fw-semibold">Standard-Spalten <span className="fw-normal text-muted">(kommagetrennt)</span></label>
              <textarea className="form-control form-control-sm font-monospace" rows={2} value={defaultFields} onChange={(e) => setDefaultFields(e.target.value)} placeholder="startDateTime, inboundSourceDialString, ..." />
            </div>
            <div className="col-12 d-flex gap-4 flex-wrap">
              <div className="form-check form-switch">
                <input className="form-check-input" type="checkbox" role="switch" id="missedOnly-cfg" checked={missedOnly} onChange={(e) => setMissedOnly(e.target.checked)} />
                <label className="form-check-label small" htmlFor="missedOnly-cfg">Nur verpasste (Standard)</label>
              </div>
              <div className="form-check form-switch">
                <input className="form-check-input" type="checkbox" role="switch" id="lockFields-cfg" checked={lockFields} onChange={(e) => setLockFields(e.target.checked)} />
                <label className="form-check-label small" htmlFor="lockFields-cfg">Spaltenauswahl sperren</label>
              </div>
            </div>
            <div className="col-12">
              <div className="form-text">
                Tipp: identische Standard-Spalten über alle Unternehmen + gesperrte Spaltenauswahl
                lassen alle Firmen denselben Anruf-Cache teilen.
              </div>
            </div>
          </div>
        )}

        {section === 'sitzung' && (
          <div className="row g-3" style={{ maxWidth: 700 }}>
            <div className="col-12 col-md-6">
              <label className="form-label small fw-semibold">Sitzungsdauer (Benutzer)</label>
              <select className="form-select form-select-sm" value={sessionDurationDays} onChange={(e) => setSessionDurationDays(Number(e.target.value))}>
                {[1, 3, 7, 14, 30, 90].map((d) => <option key={d} value={d}>{d} {d === 1 ? 'Tag' : 'Tage'}</option>)}
              </select>
              <div className="form-text">Admin-Sitzungen laufen immer nach 30 Min. Inaktivität ab.</div>
            </div>
            <div className="col-12 col-md-6">
              <label className="form-label small fw-semibold">Status-Gruppe</label>
              <select className="form-select form-select-sm" value={groupId} onChange={(e) => setGroupId(e.target.value)}>
                <option value="">— Keine —</option>
                {groups.map((g) => <option key={g.id} value={g.id}>{g.name}</option>)}
              </select>
              <div className="form-text">Unternehmen derselben Gruppe teilen Notizen &amp; Rückruf-Status. Filter bleiben pro Unternehmen.</div>
            </div>
          </div>
        )}
      </div>

      <div className="card-footer bg-body d-flex align-items-center gap-2 py-3">
        <button className="btn btn-primary" disabled={saving || !name.trim()} onClick={handleSave}>
          {saving ? 'Speichern…' : 'Speichern'}
        </button>
        <button className="btn btn-outline-secondary" onClick={onCancel}>Abbrechen</button>
        {!name.trim() && <span className="text-danger small ms-2">Bitte einen Namen vergeben (Stammdaten).</span>}
      </div>
    </div>
  );
}
